// src/services/searchIndexAdmin.js
import { db } from "../firebaseConfig";
import {
  collection, doc, getDocs, limit, query,
  serverTimestamp, writeBatch, startAfter
} from "firebase/firestore";

const IDX = collection(db, "search_index");
const PRODUCTS = collection(db, "products");
const CATEGORIES = collection(db, "categories");
const BRANDS = collection(db, "brands");

// --------- helpers ---------
const norm = (s) => (s || "").toString().trim();
const lower = (s) => norm(s).toLowerCase();

function tokenize(s) {
  // petits tokens utiles pour l'auto-suggest (pas du full text)
  return lower(s)
    .normalize("NFD").replace(/\p{Diacritic}/gu, "")
    .split(/[^a-z0-9]+/i)
    .filter(Boolean)
    .filter(t => t.length >= 2)
    .slice(0, 12); // limiter
}

function productToIndex(p) {
  const title = p.title || p.name || "Produit";
  return {
    type: "product",
    title,
    titleLower: lower(title),
    tokens: Array.from(new Set([
      ...tokenize(title),
      ...(p.brandName ? tokenize(p.brandName) : []),
      ...(p.categoryName ? tokenize(p.categoryName) : []),
      ...(Array.isArray(p.search) ? p.search.flatMap(tokenize) : []),
    ])).slice(0, 15),
    image: p.images?.[0] || "",
    url: `/p/${encodeURIComponent(p.slug || p.id)}`,
    popularity: Number(p.popularity || p.sales || 0) || 0,
    isActive: p.status === "active",
    refId: `products/${p.id}`,
    updatedAt: serverTimestamp(),
  };
}
function categoryToIndex(c) {
  const title = c.name || "Catégorie";
  return {
    type: "category",
    title,
    titleLower: lower(title),
    tokens: tokenize(title),
    image: c.image || "",
    url: `/catalogue?categoryId=${encodeURIComponent(c.id || c.slug)}`,
    popularity: Number(c.popularity || 0) || 0,
    isActive: c.isActive !== false,
    refId: `categories/${c.id}`,
    updatedAt: serverTimestamp(),
  };
}
function brandToIndex(b) {
  const title = b.name || "Marque";
  return {
    type: "brand",
    title,
    titleLower: lower(title),
    tokens: tokenize(title),
    image: b.logo || "",
    url: `/catalogue?brandId=${encodeURIComponent(b.id || b.slug)}`,
    popularity: Number(b.popularity || 0) || 0,
    isActive: b.isActive !== false,
    refId: `brands/${b.id}`,
    updatedAt: serverTimestamp(),
  };
}

async function* pagedSnap(colRef, pageSize = 300, constraints = []) {
  let cur = null;
  // on ordonne par createdAt si dispo, sinon by documentId fallback (pas possible ici) → on se contente de pages simples
  while (true) {
    let q = query(colRef, limit(pageSize), ...constraints);
    if (cur) q = query(q, startAfter(cur));
    const snap = await getDocs(q);
    if (snap.empty) return;
    yield snap;
    cur = snap.docs[snap.docs.length - 1];
    if (!cur || snap.size < pageSize) return;
  }
}

async function deleteCollectionInBatches(colRef, batchSize = 300) {
  // suppression “wipe” de l’index
  for await (const snap of pagedSnap(colRef, batchSize)) {
    const batch = writeBatch(db);
    snap.docs.forEach(d => batch.delete(d.ref));
    await batch.commit();
  }
}

async function addInBatches(items, batchSize = 400) {
  let i = 0;
  while (i < items.length) {
    const batch = writeBatch(db);
    for (let j = i; j < Math.min(i + batchSize, items.length); j++) {
      const it = items[j];
      const ref = doc(IDX); // id auto
      batch.set(ref, it);
    }
    await batch.commit();
    i += batchSize;
  }
}

// --------- API publique ---------

/**
 * Reconstruit l'index search_index à partir des collections products/categories/brands.
 * @param {Object} opts
 * @param {boolean} opts.wipe - supprime tout l’index avant de reconstruire
 * @param {boolean} opts.onlyActive - n’indexer que les produits status=active
 * @returns {Object} { counts: {products, categories, brands, indexed}, wiped: boolean }
 */
export async function rebuildSearchIndex({ wipe = false, onlyActive = true } = {}) {
  if (wipe) await deleteCollectionInBatches(IDX);

  // 1) collecter produits
  const products = [];
  for await (const snap of pagedSnap(PRODUCTS, 400)) {
    for (const d of snap.docs) {
      const p = { id: d.id, ...d.data() };
      if (onlyActive && p.status !== "active") continue;
      products.push(productToIndex(p));
    }
  }

  // 2) collecter catégories
  const categories = [];
  for await (const snap of pagedSnap(CATEGORIES, 400)) {
    for (const d of snap.docs) {
      const c = { id: d.id, ...d.data() };
      categories.push(categoryToIndex(c));
    }
  }

  // 3) collecter marques
  const brands = [];
  for await (const snap of pagedSnap(BRANDS, 400)) {
    for (const d of snap.docs) {
      const b = { id: d.id, ...d.data() };
      brands.push(brandToIndex(b));
    }
  }

  // 4) push en batch
  const all = [...products, ...categories, ...brands];
  await addInBatches(all, 400);

  return {
    wiped: !!wipe,
    counts: {
      products: products.length,
      categories: categories.length,
      brands: brands.length,
      indexed: all.length,
    },
  };
}
